import {
  Component,
  DestroyRef,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { HttpErrorResponse } from "@angular/common/http";
import { NgForm } from "@angular/forms";
import {
  PoComboOption,
  PoModalAction,
  PoModalComponent,
  PoNotificationService,
  PoProgressStatus,
  PoRadioGroupOption,
  PoSelectOption,
} from "@po-ui/ng-components";
import {
  catchError,
  concatMap,
  EMPTY,
  expand,
  forkJoin,
  iif,
  map,
  of,
  takeWhile,
  tap,
  throwError,
  timer,
  timeout,
  Subscription,
  Observable,
} from "rxjs";
import { MainService } from "../../../../app.service";
import { DependencyTrackProjectsService } from "../../services/dependency-track-projects/dependency-track-projects.service";
import { RpoOptions } from "../../enums/rpo-options";
import { GiteaUser } from "../../interfaces/gitea-user";
import { GiteaRepo } from "../../interfaces/gitea-repo";
import { Project } from "../../interfaces/project";
import { ProjectOperations } from "../../enums/project-operations";
import { Status } from "../../enums/status";
import { DependencyTrackPlatform } from "../../enums/dependency-track-platform";
import { DependencyTrackGiteaService } from "../../services/dependency-track-gitea/dependency-track-gitea.service";
import { DependencyTrackDroneService } from "../../services/dependency-track-drone/dependency-track-drone.service";

const DEPENDENCY_TRACK_USER = "dependency-track";

type PoComboOptionGroup = {
  label: string;
  options: Array<{
    value: string;
    label: string;
    description: string;
    url: string;
    branch: string;
  }>;
};

@Component({
  selector: "app-dependency-track-project",
  templateUrl: "./dependency-track-project.component.html",
  styleUrls: ["./dependency-track-project.component.css"],
})
export class DependencyTrackProjectComponent implements OnInit, OnDestroy {
  @Output() onSave: EventEmitter<Project> = new EventEmitter();

  @ViewChild(PoModalComponent, { static: true })
  private poModal: PoModalComponent;

  @ViewChild("projectForm", { static: false }) projectForm: NgForm;

  projectId: string = "";
  name: string = "";
  productOwner: string = "";
  team: string = "";
  repository: string = "";
  rpo: string = "";
  branch: string = "";
  status: string = "";
  createdBy: string = "";
  title: string = "";
  platform: string = "";
  progressText: string = "";
  progressInfo: string = "";
  pullRequestBranch: string = "";
  helpBranch: string = "";
  helpRepository: string = "";
  processError: string = "";
  progressValue: number = 0;
  repositoryIsDisabled: boolean = true;
  branchIsDisabled: boolean = true;
  projectIsReadOnly: boolean = false;
  addingProject: boolean = false;
  progressIndeterminate: boolean = true;
  modalHideClose: boolean = false;
  projectIsDisabled: boolean = true;
  showProjectCombo: boolean = true;
  repositoryIsReadOnly: boolean = true;
  platformIsReadOnly: boolean = false;
  branchIsReadOnly: boolean = true;
  platformIsDisabled: boolean = false;
  rpoOptions: Array<PoRadioGroupOption> = [];
  projectOptions: Array<PoComboOptionGroup> = [];
  teamOptions: Array<PoComboOption> = [];
  platformOptions: Array<PoSelectOption> = [];
  rpoOptionsEnum: typeof RpoOptions = RpoOptions;
  statusOptionsEnum: typeof Status = Status;
  modalPrimaryAction: PoModalAction;
  modalSecondaryAction: PoModalAction;
  operation: ProjectOperations;
  progressStatus: PoProgressStatus;
  project: Project;

  private giteaUser: GiteaUser;
  private subscriptions: Subscription = new Subscription();

  constructor(
    private destroyRef: DestroyRef,
    private mainService: MainService,
    private poNotificationService: PoNotificationService,
    private dependencyTrackDroneService: DependencyTrackDroneService,
    private dependencyTrackGiteaService: DependencyTrackGiteaService,
    private dependencyTrackProjectsService: DependencyTrackProjectsService
  ) { }

  ngOnInit(): void {
    this.setProjectOptions();
    this.setTeamOptions();
    this.setProjectOwner();
    this.setRpoOptions();
    this.setModalActions();
    this.setPlatformOptions();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  open(operation: ProjectOperations, project?: Project): void {
    this.operation = operation;
    this.projectIsReadOnly = operation === ProjectOperations.Edit;
    this.title =
      operation === ProjectOperations.Add
        ? "Adicionar projeto"
        : "Editar projeto";
    this.modalHideClose = false;

    this.projectForm.resetForm();

    if (operation === ProjectOperations.Edit && project) {
      this.projectId = project.id || "";
      this.createdBy = project.created_by;
      this.repositoryIsDisabled = !this.repository;
      this.branchIsDisabled = !this.branch;
      this.projectIsDisabled = true;
      this.platformIsDisabled = true;

      this.projectForm.setValue({
        platform: project.platform || "",
        name: project.name || "",
        productOwner: project.product_owner || "",
        team: project.team || "",
        repository: project.repository || "",
        rpo: project.rpo || "",
        branch: project.branch || "",
        status: project.status || Status.Active,
      });
    }

    this.initProjectFormValidation();
    this.setModalActions();
    this.poModal.open();
  }

  close(): void {
    this.poModal.close();
  }

  onClose(): void {
    this.addingProject = false;
    this.processError = "";
  }

  onProjectSearchChange(search: string): void {
    if (!search) {
      this.branch = "";
      this.repository = "";
    }

    if (search) {
      const value = search.split("/");
      const option = this.projectOptions.find(
        (option) => option.label === value[0]
      );
      const repository = option?.options.find(
        (option) => option.value === search
      );

      this.repository = repository?.url || "";
      this.branch = repository?.branch || "";
    }

    this.repositoryIsDisabled = !this.repository;
    this.branchIsDisabled = !this.branch;
  }

  onPlatformChange(platform: DependencyTrackPlatform): void {
    if (!platform) {
      return;
    }

    this.projectForm.controls["name"].setValue("");
    this.projectForm.controls["repository"].setValue("");
    this.projectForm.controls["branch"].setValue("");

    if (platform === DependencyTrackPlatform.GITEA) {
      this.helpRepository = "";
      this.helpBranch = "";

      this.showProjectCombo = true;
      this.repositoryIsDisabled = true;
      this.repositoryIsReadOnly = true;
      this.projectIsDisabled = false;
      this.branchIsReadOnly = true;
      return;
    }

    this.helpRepository =
      "Exemplo: https://totvstfs.visualstudio.com/project/_git/repository";
    this.helpBranch = "Exemplo: main, master, develop...";

    this.showProjectCombo = false;
    this.repositoryIsDisabled = false;
    this.repositoryIsReadOnly = false;
    this.projectIsDisabled = true;
    this.branchIsReadOnly = false;
  }

  onRepositoryChange(repository: string): void {
    if (this.platform === DependencyTrackPlatform.AZURE_DEVOPS) {
      const [beforePart, afterPart] = repository.split("/_git/");
      const before = beforePart.split("/").pop() ?? "";
      const beforeDecoded = decodeURIComponent(before);
      const after = afterPart;
      const name = `${beforeDecoded}/${after}`;

      this.projectIsDisabled = false;
      this.projectIsReadOnly = true;

      this.projectForm.controls["name"].setValue(name);

      if (!this.validateRepositoryUrl(repository)) {
        this.projectForm.controls["repository"].setErrors({ invalid: true });
        return;
      }
    }
  }

  validateRepositoryUrl(repository: string): boolean {
    if (this.platform === DependencyTrackPlatform.AZURE_DEVOPS) {
      const azureRegex =
        /^https:\/\/totvstfs\.visualstudio\.com\/[^\/]+\/_git\/[^\/]+$/;

      if (!azureRegex.test(repository)) {
        return false;
      }
    }

    return true;
  }

  private setRpoOptions(): void {
    this.rpoOptions = [
      { label: "Sim", value: RpoOptions.Yes },
      { label: "Não", value: RpoOptions.No },
    ];
  }

  private setPlatformOptions(): void {
    this.platformOptions = [
      { label: "Gitea", value: DependencyTrackPlatform.GITEA },
      { label: "Azure DevOps", value: DependencyTrackPlatform.AZURE_DEVOPS },
    ];
  }

  private setProjectOptions(): void {
    const giteaUser = sessionStorage.getItem("giteaUser");

    if (giteaUser) {
      this.giteaUser = JSON.parse(giteaUser) as GiteaUser;

      this.loadPaginatedRepos(this.giteaUser.id.toString());
      return;
    }

    this.dependencyTrackGiteaService
      .getGiteaUsers()
      .pipe(
        tap((user) => {
          sessionStorage.setItem("giteaUser", JSON.stringify(user));
          this.giteaUser = user;
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: (user) => {
          this.loadPaginatedRepos(user.id.toString());
        },
      });
  }

  private loadPaginatedRepos(userId: string): void {
    const limit = 30;
    let allProjects: GiteaRepo[] = [];

    this.fetchReposPage(userId, 1, limit, allProjects);
  }

  private fetchReposPage(
    userId: string,
    page: number,
    limit: number,
    accumulated: GiteaRepo[]
  ): void {
    this.dependencyTrackGiteaService
      .getGiteaReposSearch(userId, page, limit)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          const newAccumulated = [...accumulated, ...response.items];

          this.updateProjectOptions(newAccumulated);

          if (response.has_next) {
            this.fetchReposPage(userId, page + 1, limit, newAccumulated);
          }
        },
        error: (error) => {
          this.poNotificationService.error(
            "Houve uma falha ao carregar os repositórios do Gitea. Tente novamente."
          );
        },
      });
  }

  private updateProjectOptions(projects: GiteaRepo[]): void {
    const groupedProjects = projects.reduce((acc, project) => {
      const ownerName = project.owner.username;
      if (!acc[ownerName]) {
        acc[ownerName] = [];
      }
      acc[ownerName].push({
        value: project.full_name,
        label: project.full_name,
        description: project.description,
        url: project.html_url,
        branch: project.default_branch,
      });
      return acc;
    }, {} as Record<string, any[]>);

    this.projectOptions = Object.keys(groupedProjects).map((ownerName) => ({
      label: ownerName,
      options: groupedProjects[ownerName],
    }));
  }

  private setTeamOptions(): void {
    this.mainService.getAreas().subscribe({
      next: (areas) => {
        this.teamOptions = areas.map(({ area }) => ({
          label: area,
          value: area,
        }));
      },
    });
  }

  private setModalActions(): void {
    this.modalPrimaryAction = {
      label: this.operation === ProjectOperations.Add ? "Adicionar" : "Salvar",
      action: () => {
        if (this.projectForm.valid) {
          const formValues = { ...this.projectForm.value }; // cópia dos valores do formulário para gerar nova referência na memória

          this.modalHideClose = true;
          this.modalSecondaryAction.disabled = true;
          this.modalPrimaryAction.loading = true;

          if (this.operation === ProjectOperations.Edit) {
            this.dependencyTrackProjectsService
              .putProjects({
                id: this.projectId,
                platform: formValues.platform,
                product_owner: formValues.productOwner,
                branch: formValues.branch,
                repository: formValues.repository,
                rpo: formValues.rpo,
                name: formValues.name,
                team: formValues.team,
                status: formValues.status,
                created_by: this.createdBy,
              })
              .subscribe({
                next: (project) => {
                  this.onSave.emit(project);
                },
                complete: () => {
                  this.poNotificationService.success(
                    "Projeto editado com sucesso!"
                  );
                  this.close();
                  this.modalPrimaryAction.loading = false;
                },
                error: () => {
                  this.poNotificationService.error(
                    "Houve uma falha ao editar o projeto. Tente novamente."
                  );
                  this.close();
                  this.modalPrimaryAction.loading = false;
                },
              });
            return;
          }

          this.addingProject = true;
          this.progressIndeterminate = true;
          this.title = "Adicionando projeto...";
          this.modalPrimaryAction.label = "Fechar";
          this.progressStatus = PoProgressStatus.Default;

          if (formValues.platform === DependencyTrackPlatform.AZURE_DEVOPS) {
            of(null)
              .pipe(
                tap(() => {
                  if (
                    !formValues.name ||
                    !formValues.repository ||
                    !formValues.branch
                  ) {
                    this.poNotificationService.error(
                      "Dados obrigatórios do projeto não foram preenchidos."
                    );
                  }

                  this.progressText =
                    "Adicionando projeto no Dependency Track...";
                  this.progressInfo = "Etapa 1 de 1";
                }),
                concatMap(() =>
                  this.dependencyTrackProjectsService.postProjects({
                    platform: formValues.platform,
                    product_owner: formValues.productOwner,
                    branch: formValues.branch,
                    repository: formValues.repository,
                    rpo: formValues.rpo,
                    name: formValues.name,
                    team: formValues.team,
                    status: formValues.status,
                    created_by: this.createdBy,
                  })
                ),
                tap((project) => {
                  this.project = project;
                }),
                takeUntilDestroyed(this.destroyRef)
              )
              .subscribe({
                next: (project) => this.onSave.emit(project),
                complete: () => {
                  this.modalPrimaryAction.loading = false;
                  this.modalPrimaryAction.disabled = false;
                  this.modalPrimaryAction.action = () => this.close();
                  this.progressStatus = PoProgressStatus.Success;
                  this.progressText = "";
                  this.progressInfo = "Processo concluído!";
                  this.progressIndeterminate = false;
                  this.progressValue = 100;
                  this.modalHideClose = false;
                  this.addingProject = false;

                  this.close();

                  const successMessage = "Projeto adicionado com sucesso!";
                  this.poNotificationService.success(successMessage);
                },
                error: (err: HttpErrorResponse) => {
                  this.modalPrimaryAction.loading = false;
                  this.modalPrimaryAction.disabled = false;
                  this.modalPrimaryAction.action = () => this.close();
                  this.progressStatus = PoProgressStatus.Error;
                  this.progressText = "";
                  this.progressInfo = "Falha no processo!";
                  this.progressIndeterminate = false;
                  this.progressValue = 100;
                  this.modalHideClose = false;

                  this.poNotificationService.error(
                    "Houve uma falha ao adicionar o projeto. Tente novamente."
                  );
                },
              });
            return;
          }

          of(null)
            .pipe(
              tap(() => {
                if (
                  !formValues.name ||
                  !formValues.repository ||
                  !formValues.branch
                ) {
                  this.poNotificationService.error(
                    "Dados obrigatórios do projeto não foram preenchidos."
                  );
                }
              }),
              tap(() => {
                this.progressText = `Analisando arquivo package-lock.json na branch ${formValues.branch} do repositório...`;
                this.progressInfo = "Etapa 1 de 9";
              }),
              concatMap(() =>
                this.dependencyTrackGiteaService
                  .getGiteaReposContents(formValues.name, formValues.branch)
                  .pipe(
                    catchError((err) => {
                      if (err?.status === 404) {
                        this.processError = `Arquivo <b>package-lock.json</b> não encontrado na branch <b>${formValues.branch}</b> do repositório <b><a href="${formValues.name}" target="_blank">${formValues.repository}</a></b>. Por favor, retire-o do <b>.gitignore</b>, faça o commit na branch <b>${formValues.branch}</b> e tente novamente.`;
                      }

                      return throwError(() => err);
                    })
                  )
              ),
              tap(() => {
                this.progressText =
                  "Adicionando projeto no Dependency Track...";
                this.progressInfo = "Etapa 2 de 9";
              }),
              concatMap(() =>
                this.dependencyTrackProjectsService.postProjects({
                  platform: formValues.platform,
                  product_owner: formValues.productOwner,
                  branch: formValues.branch,
                  repository: formValues.repository,
                  rpo: formValues.rpo,
                  name: formValues.name,
                  team: formValues.team,
                  status: formValues.status,
                  created_by: this.createdBy,
                })
              ),
              tap((project) => {
                if (!project || !project.name || !project.id) {
                  this.poNotificationService.error(
                    "Falha na criação do projeto. Tente novamente."
                  );
                }

                this.project = project;


                this.progressInfo = "Projeto associado com sucesso!";
              }),

              concatMap(() =>
                of(null).pipe(
                  tap(() => {
                    this.progressText =
                      "Adicionando usuário dependency-track ao repositório no Gitea...";
                    this.progressInfo = "Etapa 3 de 9";
                  }),
                  concatMap(() =>
                    this.dependencyTrackGiteaService.putGiteaReposCollaborators(
                      this.project.name,
                      DEPENDENCY_TRACK_USER,
                      {
                        permission: "admin",
                      }
                    )
                  ),
                  tap(() => {
                    this.progressText = "Sincronizando repositório no Drone...";
                    this.progressInfo = "Etapa 4 de 9";
                  }),
                  concatMap(() =>
                    this.dependencyTrackDroneService.postDroneUserRepos(true)
                  ),
                  concatMap(() =>
                    this.dependencyTrackDroneService.getDroneUser().pipe(
                      expand(({ syncing }) =>
                        syncing
                          ? timer(2000).pipe(
                            concatMap(() =>
                              this.dependencyTrackDroneService.getDroneUser()
                            )
                          )
                          : EMPTY
                      ),
                      takeWhile(({ syncing }) => syncing, true),
                      timeout(60000),
                      catchError(() => {
                        return of({ syncing: false });
                      }),
                      map(() => void 0)
                    )
                  ),
                  tap(() => {
                    this.progressText = "Ativando o repositório no Drone...";
                    this.progressInfo = "Etapa 5 de 9";
                  }),
                  concatMap(() =>
                    this.dependencyTrackDroneService.postDroneRepos(
                      this.project.name
                    )
                  ),
                  tap(() => {
                    this.progressText =
                      "Removendo secrets conflitantes do repositório no Drone...";
                    this.progressInfo = "Etapa 6 de 9";
                  }),
                  concatMap(() =>
                    this.dependencyTrackDroneService.deleteDroneReposSecrets(
                      this.project.name
                    )
                  ),
                  tap(() => {
                    this.progressText = "Criando secret do repositório no Drone...";
                    this.progressInfo = "Etapa 7 de 9";
                  }),
                  concatMap(() =>
                    this.dependencyTrackDroneService.postDroneReposSecrets(
                      this.project.name,
                      this.project.id || ""
                    )
                  ),
                  tap(() => {
                    this.progressText =
                      "Adicionando scripts dependencytrack.sh e .drone.yml no repositório...";
                    this.progressInfo = "Etapa 8 de 9";
                  }),
                  concatMap(() =>
                    this.dependencyTrackGiteaService.postGiteaReposContents(
                      this.project.name,
                      this.project.branch
                    ).pipe(
                      tap((response) => {
                        // response agora é HttpResponse<Array<GiteaReposContent>>
                        // response.status contém o código HTTP (200, 201, etc)
                        if (response.status === 200) {
                          // Arquivo foi atualizado (200)
                          this.progressText = "Arquivos já estão atualizados no repositório";
                          this.progressInfo = "Etapa 9 de 9";
                        }
                        // 201 = arquivo criado, continua normalmente
                      }),
                      catchError((err: HttpErrorResponse) => {
                        return throwError(() => err);
                      })
                    )
                  ),
                  concatMap((response) => {
                    // Se retornar 200 (arquivo atualizado), pula a criação do pull request
                    if (response.status === 200) {
                      return of(null);
                    }

                    // Cria pull request para 201 (arquivo criado)
                    return of(null).pipe(
                      tap(() => {
                        this.progressText = "Criando pull request no Gitea...";
                        this.progressInfo = "Etapa 9 de 9";
                      }),
                      concatMap(() =>
                        this.dependencyTrackGiteaService
                          .postGiteaReposPulls(this.project.name, this.project.branch)
                          .pipe(
                            catchError((err: HttpErrorResponse) => {
                              if (err?.status === 204) {
                                return of(null);
                              }
                              return throwError(() => err);
                            }),
                            tap((response) => {
                              if (response?.head) {
                                this.pullRequestBranch = response.head.ref;
                              }
                            })
                          )
                      )
                    );
                  })
                )
              ),
              catchError((err: HttpErrorResponse) => {
                const deleteRequests: Observable<any>[] = [];

                if (this.project?.name && this.pullRequestBranch) {
                  deleteRequests.push(
                    this.dependencyTrackGiteaService
                      .deleteGiteaReposBranch(
                        this.project.name,
                        this.pullRequestBranch
                      )
                      .pipe(
                        catchError((deleteErr) => {
                          return of(null);
                        })
                      )
                  );
                }

                if (!deleteRequests.length) {
                  return throwError(() => err);
                }

                return forkJoin(deleteRequests).pipe(
                  concatMap(() => {
                    return throwError(() => err);
                  }),
                  catchError(() => {
                    return throwError(() => err);
                  })
                );
              }),
              takeUntilDestroyed(this.destroyRef)
            )
            .subscribe({
              complete: () => {
                this.modalPrimaryAction.loading = false;
                this.modalPrimaryAction.disabled = false;
                this.modalPrimaryAction.action = () => this.close();
                this.progressStatus = PoProgressStatus.Success;
                this.progressText = "";
                this.progressInfo = "Processo concluído!";
                this.progressIndeterminate = false;
                this.progressValue = 100;
                this.modalHideClose = false;
                this.addingProject = false;

                this.onSave.emit(this.project);
                this.close();

                const successMessage = "Projeto adicionado com sucesso!";
                this.poNotificationService.success(successMessage);
              },
              error: (err: HttpErrorResponse) => {
                this.modalPrimaryAction.loading = false;
                this.modalPrimaryAction.disabled = false;
                this.modalPrimaryAction.action = () => this.close();
                this.progressStatus = PoProgressStatus.Error;
                this.progressText = "";
                this.progressInfo = "Falha no processo!";
                this.progressIndeterminate = false;
                this.progressValue = 100;
                this.modalHideClose = false;

                this.poNotificationService.error(
                  "Houve uma falha ao adicionar o projeto. Tente novamente."
                );
              },
            });
        }
      },
      loading: false,
      disabled: true,
    };
    this.modalSecondaryAction = {
      label: "Cancelar",
      action: () => this.close(),
    };
  }

  private initProjectFormValidation(): void {
    this.subscriptions.add(
      this.projectForm?.statusChanges?.subscribe({
        next: (status: string) => {
          this.modalPrimaryAction.disabled =
            status !== "VALID" ||
            (status === "VALID" && (this.projectForm?.untouched ?? true));
        },
      })
    );
  }

  private setProjectOwner(): void {
    this.createdBy =
      sessionStorage.getItem("User") || this.giteaUser?.username || "";
  }
}
